import { useCallback, useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";

interface WaveformVisualizerProps {
  source: string | ArrayBuffer;
  height?: number;
  waveColor?: string;
  progressColor?: string;
  cursorColor?: string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

export const WaveformVisualizer = ({
  source,
  height = 80,
  waveColor = "#71717a",
  progressColor = "#eab308",
  cursorColor = "#eab308",
  onLoad,
  onError,
}: WaveformVisualizerProps) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const waveSurfer = useRef<WaveSurfer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const initializeWaveform = useCallback(() => {
    if (!waveformRef.current) return;

    console.log("Initializing waveform with source type:", typeof source);

    try {
      waveSurfer.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor,
        progressColor,
        cursorColor,
        height,
        normalize: true,
        barWidth: 2,
        barGap: 1,
        barRadius: 2,
        minPxPerSec: 50,
        fillParent: true,
      });

      waveSurfer.current.on("ready", () => {
        console.log("Waveform ready");
        setIsLoading(false);
        onLoad?.();
      });

      waveSurfer.current.on("error", (err) => {
        console.error("WaveSurfer error:", err);
        setError(new Error("Failed to load audio"));
        onError?.(err);
      });

      if (typeof source === "string") {
        console.log("Loading from URL:", source);
        waveSurfer.current.load(source);
      } else if (source instanceof ArrayBuffer) {
        console.log("Loading from ArrayBuffer, length:", source.byteLength);
        const blob = new Blob([source], { type: "audio/mpeg" });
        waveSurfer.current.loadBlob(blob);
      } else {
        throw new Error("Invalid source type");
      }
    } catch (err) {
      // console.error("WaveSurfer initialization error:", err);
      setError(err instanceof Error ? err : new Error("Failed to initialize waveform"));
      onError?.(err instanceof Error ? err : new Error("Failed to initialize waveform"));
    }
  }, [source, height, waveColor, progressColor, cursorColor, onLoad, onError]);

  useEffect(() => {
    initializeWaveform();

    return () => {
      if (waveSurfer.current) {
        waveSurfer.current.destroy();
        waveSurfer.current = null;
      }
    };
  }, [initializeWaveform]);

  if (!source) {
    return <div className="text-zinc-500">No audio source available</div>;
  }

  return (
    <div className="relative">
      <div ref={waveformRef} className="w-full" />
      {isLoading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-800/50 rounded-lg">
          <div className="animate-pulse text-zinc-500">Loading waveform...</div>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-800/50 rounded-lg">
          <p className="text-zinc-500">Failed to load waveform: {error.message}</p>
        </div>
      )}
    </div>
  );
};
